import { Link } from "react-router-dom";
import { Lock, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

export type StudyMode = {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Tailwind text color class for the icon, e.g. "text-blue-600" */
  iconColor: string;
  /** Tailwind background tint class, e.g. "bg-blue-500/10" */
  iconBg: string;
  /** If true, accessible even without subscription (e.g., AI Tutor) */
  alwaysAccessible?: boolean;
};

type Props = {
  mode: StudyMode;
  href: string;
  locked?: boolean;
  onLockedClick?: () => void;
  progressLabel?: string;
};

export function StudyModeCard({ mode, href, locked, onLockedClick, progressLabel }: Props) {
  const inner = (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between">
        <div
          className={cn(
            "grid h-11 w-11 place-items-center rounded-xl",
            mode.iconBg,
            mode.iconColor,
          )}
        >
          <mode.icon className="h-5 w-5" />
        </div>
        {locked ? (
          <span className="grid h-7 w-7 place-items-center rounded-full bg-surface-2 text-text-subtle">
            <Lock className="h-3.5 w-3.5" />
          </span>
        ) : null}
      </div>
      <h3 className="mt-4 font-display text-lg font-semibold tracking-tight">{mode.label}</h3>
      <p className="mt-1 text-sm text-text-muted text-pretty">{mode.description}</p>
      {progressLabel ? (
        <p className="mt-3 text-xs font-medium text-brand">{progressLabel}</p>
      ) : null}
    </div>
  );

  const className = cn(
    "block h-full rounded-xl border border-border bg-surface dark:bg-surface p-5",
    "transition-[border-color,box-shadow] duration-150",
    "hover:border-border-strong hover:shadow-sm dark:hover:shadow-none",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    locked && "opacity-90",
  );

  if (locked && onLockedClick) {
    return (
      <button type="button" onClick={onLockedClick} className={cn(className, "text-left w-full")}>
        {inner}
      </button>
    );
  }
  return (
    <Link to={href} className={className}>
      {inner}
    </Link>
  );
}
